import React from 'react';
import { Box, Typography, Chip, Tooltip, IconButton } from '@mui/material';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { NEOFEED_USAGE_MODE_LABELS, formatNeoFeedScore } from './neoFeedContextUtils';

const PRIMARY = '#f25d27';

/**
 * @typedef {import('../../../types/articles').NeoFeedSourceSnapshot} NeoFeedSourceSnapshot
 */

// Duplicado intencionalmente de `formatDate` (ArticleWriter/index.js) para
// evitar import circular entre este arquivo e o componente principal.
const formatSourceDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
    });
};

/** Card compacto de uma única fonte NeoFeed auditada (snapshot, sem chunks). */
const SourceRow = ({ source }) => {
    const usageLabel = NEOFEED_USAGE_MODE_LABELS[source.usage_mode] ?? source.usage_mode;
    const scoreLabel = formatNeoFeedScore(source.score);
    const dateLabel = formatSourceDate(source.published_at);

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            p: '8px 10px',
            borderRadius: '8px',
            border: '1px solid #eee',
            backgroundColor: '#fafafa',
        }}>
            <NewspaperOutlinedIcon sx={{ fontSize: 16, color: PRIMARY, mt: '2px', flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#333', lineHeight: 1.3 }}>
                    {source.title || `Notícia #${source.external_article_id}`}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.8, mt: 0.4 }}>
                    {source.author && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>{source.author}</Typography>
                    )}
                    {dateLabel && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#aaa' }}>{dateLabel}</Typography>
                    )}
                    <Chip
                        label={usageLabel}
                        size="small"
                        sx={{ height: 17, fontSize: '0.62rem', fontWeight: 700, backgroundColor: `${PRIMARY}15`, color: PRIMARY }}
                    />
                    {scoreLabel && (
                        <Typography sx={{ fontSize: '0.7rem', color: '#aaa' }}>{scoreLabel} relevância</Typography>
                    )}
                </Box>
            </Box>
            <Tooltip title={source.url ? 'Abrir notícia original' : 'Link não disponível'}>
                <span>
                    <IconButton
                        size="small"
                        disabled={!source.url}
                        onClick={() => window.open(source.url, '_blank', 'noopener,noreferrer')}
                        sx={{ color: '#999', mt: '-2px', '&:hover': { color: PRIMARY, backgroundColor: `${PRIMARY}10` } }}
                    >
                        <OpenInNewIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
};

/**
 * Auditoria das fontes NeoFeed usadas em uma versão da matéria (evento
 * `done` do SSE ou `versions[].neofeed_sources` no GET de detalhe). Não
 * renderiza nada quando não houver fontes.
 * @param {{ sources?: NeoFeedSourceSnapshot[] }} props
 */
export default function NeoFeedSourcesAudit({ sources }) {
    if (!Array.isArray(sources) || sources.length === 0) return null;

    return (
        <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid #f0f0f0' }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#555', mb: 1 }}>
                Fontes NeoFeed usadas nesta versão
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                {sources.map((source, idx) => (
                    <SourceRow key={`${source.external_article_id}-${idx}`} source={source} />
                ))}
            </Box>
        </Box>
    );
}
